/**
 * Service for managing AI provider operations with the ConnectPath API.
 * 
 * This service provides real API implementations for AI integration functionality
 * including provider management, church context configuration, usage tracking,
 * and AI completion generation. It communicates with the backend AI endpoints.
 * 
 * This service provides methods for:
 * - AI provider configuration management
 * - Church-specific AI context configuration
 * - Usage statistics and request logs
 * - AI completion generation
 * 
 * @example
 * const aiService = new ApiAIService('/api/v1');
 * const providers = await aiService.getProviders();
 */
import { BaseService } from '../base/BaseService';
import { IAIService } from '../../interfaces/IAIService';
import {
  AIProvider,
  AIProviderCreate,
  AIUsageStats,
  ChurchAIContext,
  AIRequestLog,
  AICompletionRequest,
  AICompletionResponse,
  FeatureAssignments,
  AssignmentUpdate,
  ProviderCapabilities,
  FeatureCoverage,
  AIFeatureInfo,
  AIFeatureArea
} from '../../../types/ai';
import { enhancedTokenManager as TokenManager } from '../../EnhancedTokenManager';

export class ApiAIService extends BaseService implements IAIService {
  constructor(private apiUrl: string) {
    super('ApiAIService');
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await TokenManager.getToken();
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Get all AI providers for the current church
   */
  async getProviders(): Promise<AIProvider[]> {
    const response = await fetch(
      `/api/v1/ai/providers/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch AI providers');
    }

    return response.json();
  }

  /**
   * Get a specific AI provider by ID
   */
  async getProvider(id: string): Promise<AIProvider> {
    const response = await fetch(
      `/api/v1/ai/providers/${id}/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch AI provider');
    }

    return response.json();
  }

  /**
   * Create a new AI provider configuration
   */
  async createProvider(data: AIProviderCreate): Promise<AIProvider> {
    const response = await fetch(
      `/api/v1/ai/providers/`,
      {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create AI provider');
    }

    return response.json();
  }

  /**
   * Update an existing AI provider configuration
   */
  async updateProvider(id: string, data: Partial<AIProvider>): Promise<AIProvider> {
    const response = await fetch(
      `/api/v1/ai/providers/${id}/`,
      {
        method: 'PATCH',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update AI provider');
    }

    return response.json();
  }

  /**
   * Delete an AI provider configuration
   */
  async deleteProvider(id: string): Promise<void> {
    const response = await fetch(
      `/api/v1/ai/providers/${id}/`,
      {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete AI provider');
    }
  }

  /**
   * Test an AI provider connection
   */
  async testProvider(id: string, testPrompt?: string): Promise<{ success: boolean; error?: string }> {
    const response = await fetch(
      `/api/v1/ai/providers/${id}/test_connection/`,
      {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ 
          provider_id: id,
          test_prompt: testPrompt 
        })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to test AI provider');
    }

    return response.json();
  }

  /**
   * Get church-specific AI context
   */
  async getChurchContext(): Promise<ChurchAIContext> {
    const response = await fetch(
      `/api/v1/ai/context/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch church AI context');
    }

    return response.json();
  }

  /**
   * Update church-specific AI context
   */
  async updateChurchContext(data: Partial<ChurchAIContext>): Promise<ChurchAIContext> {
    const response = await fetch(
      `/api/v1/ai/context/`,
      {
        method: 'PATCH',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update church AI context');
    }

    return response.json();
  }

  /**
   * Get usage statistics for AI providers
   */
  async getUsageStats(days?: number): Promise<AIUsageStats[]> {
    const params = new URLSearchParams();
    if (days) {
      params.append('days', days.toString());
    }

    const response = await fetch(
      `/api/v1/ai/providers/usage_stats/?${params}`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch usage statistics');
    }

    return response.json();
  }

  /**
   * Get provider-specific usage statistics filtered by time period
   */
  async getProviderUsageStats(providerId: string, period: string, year?: number): Promise<{
    period: string;
    year: number;
    total_requests: number;
    total_tokens: number;
    total_cost: number;
    start_date: string;
    end_date: string;
    monthly_breakdown?: Array<{
      month: string;
      requests: number;
      tokens: number;
      cost: number;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append('period', period);
    if (year) {
      params.append('year', year.toString());
    }

    const response = await fetch(
      `/api/v1/ai/providers/${providerId}/usage_stats/?${params}`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch provider usage statistics');
    }

    return response.json();
  }

  /**
   * Get AI request logs with optional filters
   */
  async getRequestLogs(filters?: {
    provider?: string;
    feature?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<AIRequestLog[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }

    const response = await fetch(
      `/api/v1/ai/logs/?${params}`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch request logs');
    }

    return response.json();
  }

  /**
   * Generate AI completion using configured providers
   */
  async generateCompletion(
    prompt: string,
    feature: AIFeatureArea = AIFeatureArea.WEB_INTERFACE,
    context?: Record<string, any>,
    stream: boolean = false,
    providerId?: number
  ): Promise<AICompletionResponse> {
    const payload: any = {
      prompt,
      feature,
      context,
      stream
    };
    
    if (providerId) {
      payload.provider_id = providerId;
    }

    const response = await fetch(
      `/api/v1/ai/complete/`,
      {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate AI completion');
    }

    return response.json();
  }

  /**
   * Get feature assignments for the current church
   */
  async getFeatureAssignments(): Promise<{
    assignments: FeatureAssignments;
    available_providers: Array<{id: number; name: string; provider_type: string}>;
    uses_connectpath_managed: boolean;
  }> {
    const response = await fetch(
      `/api/v1/ai/providers/assignments/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get feature assignments');
    }

    return response.json();
  }

  /**
   * Update feature assignments
   */
  async updateFeatureAssignments(update: AssignmentUpdate): Promise<{
    success: boolean;
    assignments: FeatureAssignments;
  }> {
    const response = await fetch(
      `/api/v1/ai/providers/assignments/`,
      {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(update)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update feature assignments');
    }

    return response.json();
  }

  /**
   * Get provider capabilities
   */
  async getProviderCapabilities(providerId: number): Promise<ProviderCapabilities> {
    const response = await fetch(
      `/api/v1/ai/providers/${providerId}/capabilities/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get provider capabilities');
    }

    return response.json();
  }

  /**
   * Get feature coverage information
   */
  async getFeatureCoverage(): Promise<FeatureCoverage> {
    const response = await fetch(
      `/api/v1/ai/providers/coverage/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get feature coverage');
    }

    return response.json();
  }

  /**
   * Get AI features list
   */
  async getAIFeatures(): Promise<{
    features: AIFeatureInfo[];
    total_features: number;
    platform_features: number;
    user_language_level: string;
  }> {
    const response = await fetch(
      `/api/v1/ai/features/`,
      {
        method: 'GET',
        headers: await this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get AI features');
    }

    return response.json();
  }

  /**
   * Fetch available models for a specific provider type
   */
  async fetchAvailableModels(providerType: string, apiKey: string): Promise<{
    success: boolean;
    models?: Array<{ value: string; label: string }>;
    error?: string;
  }> {
    try {
      const response = await fetch(
        `/api/v1/ai/fetch-models/`,
        {
          method: 'POST',
          headers: await this.getAuthHeaders(),
          body: JSON.stringify({
            provider_type: providerType,
            api_key: apiKey
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || 'Failed to fetch models'
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch models'
      };
    }
  }

  async fetchAvailableModelsForProvider(providerId: string): Promise<{
    success: boolean;
    models?: Array<{ value: string; label: string }>;
    error?: string;
  }> {
    try {
      const token = await TokenManager.getToken();
      if (!token) {
        return {
          success: false,
          error: 'Authentication required'
        };
      }

      const response = await fetch(`/api/v1/ai/providers/${providerId}/fetch_models/`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || 'Failed to fetch models'
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch models'
      };
    }
  }
}